import React, { useState, useEffect } from "react";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";

const ManageStudentPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    studentName: "",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const navigate = useNavigate();

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(filters.studentName && { studentName: filters.studentName }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
      }).toString();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/students/summary?${query}`
      );
      const result = await response.json();

      setStudents(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (err) {
      console.error("Error fetching students:", err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStudents();
  }, [page, limit, filters]);

  const handleSearch = () => {
    setPage(1);
    fetchStudents();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const confirmDelete = (id) => {
    setStudentToDelete(id);
  };

  const handleDelete = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/students/${studentToDelete}`,
        {
          method: "DELETE",
        }
      );
      setStudentToDelete(null);
      fetchStudents();
    } catch (err) {
      console.error("Error deleting student:", err);
    }
  };

  const confirmBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(
        selectedStudents.map((id) =>
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/${id}`, {
            method: "DELETE",
          })
        )
      );
      setSelectedStudents([]);
      setSelectAll(false);
      setShowBulkDeleteModal(false);
      fetchStudents();
    } catch (err) {
      console.error("Error deleting selected students:", err);
    }
  };

  const handleSelectAll = () => {
    const allIds = students.map((s) => s.id);
    setSelectAll(!selectAll);
    setSelectedStudents(selectAll ? [] : allIds);
  };

  const toggleSelection = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };
  const handleClear = () => {
    setFilters({ dateFrom: "", dateTo: "", studentName: "" });
    setPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [
        [
          "Student Name",
          "Student Email",
          "Phone Number",
          "Total Attendance",
          "Total Absent",
        ],
      ],
      body: students.map((student) => [
        student.name,
        student.email,
        student.phone,
        student.total_attendance,
        student.total_absent,
      ]),
    });
    doc.save("students_summary.pdf");
  };

  const exportToExcel = () => {
    const csvContent = [
      [
        "Student Name",
        "Student Email",
        "Phone Number",
        "Total Attendance",
        "Total Absent",
      ],
      ...students.map((student) => [
        student.name,
        student.email,
        student.phone,
        student.total_attendance,
        student.total_absent,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "students_summary.csv");
  };

  return (
    <>
      <header>
        <div className="homebanner">
          <img src="homebanner.png" alt="HomePage Banner" />
        </div>
        <div className="homelogo">
          <img src="church_logo_no_bg.png" alt="Church Logo" />
        </div>
        <div className="cross_icon">
          <img src="cross_icon.png" alt="Cross Icon" />
        </div>
      </header>

      <footer>
        <div className="homebody">
          <button
            onClick={() => navigate("/HomePage")}
            className="mb-4 text-blue-600 hover:underline"
          >
            ← Back to Dashboard
          </button>

          <div className="managebody">
            <h1>Manage Students</h1>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            className="filter-section flex flex-wrap gap-2 items-center"
          >
            <label className="text-sm font-medium">Start Date</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleChange}
            />
            <label className="text-sm font-medium">End Date</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleChange}
            />
            <input
              type="text"
              name="studentName"
              placeholder="Student Name"
              value={filters.studentName}
              onChange={handleChange}
            />
            <button onClick={handleSearch}>Search</button>

            <button
              onClick={handleClear}
              className="bg-gray-300 px-3 py-1 rounded"
              style={{ marginLeft: "0.5rem" }}
            >
              Clear
            </button>
          </form>

          {selectedStudents.length > 0 && (
            <div className="mt-4 flex justify-end">
              <button
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                onClick={confirmBulkDelete}
              >
                Delete Selected
              </button>
            </div>
          )}

          <div className="table-wrapper">
            {loading ? (
              <p>Loading students...</p>
            ) : students.length === 0 ? (
              <p>No students found.</p>
            ) : (
              <table className="student-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Student Name</th>
                    <th>Student Email</th>
                    <th>Phone Number</th>
                    <th>Total Attendance</th>
                    <th>Total Absent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleSelection(student.id)}
                        />
                      </td>
                      <td>{student.name}</td>
                      <td>{student.email}</td>
                      <td>{student.phone}</td>
                      <td>{student.total_attendance}</td>
                      <td>{student.total_absent}</td>
                      <td>
                        <button
                          className="text-red-600 hover:underline"
                          onClick={() => confirmDelete(student.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="export-buttons mt-4">
            <button
              onClick={exportToPDF}
              className="mr-2 bg-blue-500 text-white px-4 py-2 rounded"
            >
              Export to PDF
            </button>
            <button
              onClick={exportToExcel}
              className="bg-green-500 text-white px-4 py-2 rounded"
            >
              Export to Excel
            </button>
          </div>

          <div className="pagination mt-6">
            <button
              className="pagination_bracket"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={page === i + 1 ? "active" : ""}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination_bracket"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
        <div className="ml-4">
          <label htmlFor="limit" className="mr-2 font-medium text-sm">
            Rows per page:
          </label>
          <select
            id="limit"
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1); // Reset to page 1 when limit changes
            }}
            className="border rounded px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </footer>

      {studentToDelete !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <p className="mb-4">
              Are you sure you want to delete this student? Once deleted, it
              cannot be restored.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={() => setStudentToDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full">
            <h2 className="text-lg font-semibold mb-4">
              Confirm Bulk Deletion
            </h2>
            <p className="mb-4">
              Are you sure you want to delete the selected students? Once
              deleted, they cannot be restored.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={() => setShowBulkDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={handleDeleteSelected}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageStudentPage;
